<template>
  <ElConfigProvider :locale="ruLocale">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <ClientOnly>
      <TelegramDebugPanel />
    </ClientOnly>
  </ElConfigProvider>
</template>
<script setup lang="ts">
  import { ElConfigProvider } from "element-plus";
  import { ruLocale } from "~/bin/element-plus";
  import "dayjs/locale/ru";
  import { useGlobalSockets } from "~/composables/useGlobalSockets";
  import { useWalletStore } from "~/store/WalletModule";
  import { usePublisherStore } from "~/store/PublisherModule";

  // Canonical URL для всех страниц: абсолютный адрес текущей страницы без query.
  // Задан на уровне приложения, поэтому появляется автоматически и на новых страницах.
  // useRequestURL берёт реальный хост запроса (на SSR — из заголовков), поэтому
  // canonical всегда совпадает с доменом, по которому страницу открыли.
  const requestUrl = useRequestURL();
  const route = useRoute();
  useHead({
    link: [
      {
        rel: "canonical",
        href: computed(() => `${requestUrl.origin}${route.path}`),
      },
    ],
  });

  const walletStore = useWalletStore();

  const publisherStore = usePublisherStore();

  let globalSockets: ReturnType<typeof useGlobalSockets> | null = null;
  if (import.meta.client) {
    globalSockets = useGlobalSockets();
  }

  onMounted(() => {
    if (!globalSockets) return;
    const advertiserKey = useCookie("accessTokenSJ").value;
    const publisherKey = useCookie("authorizedP").value;
    const apiKey = advertiserKey || publisherKey;
    if (apiKey) {
      globalSockets.connect(apiKey);
    }
    globalSockets.joinGlobal();

    globalSockets.handleGlobalEvent((data: any) => {
      switch (data?.eventType) {
        case "WalletDeposit": {
          const balance = Number(data?.eventInfo?.balance);
          if (Number.isFinite(balance)) {
            walletStore.setBalance(balance);
          }
          ElNotification({
            title: "Баланс пополнен!",
            type: "success",
          });
          break;
        }
        case "AdvertiserWalletWithdrawal": {
          const balance = Number(data?.eventInfo?.balance);
          if (Number.isFinite(balance)) {
            walletStore.setBalance(balance);
          }
          ElNotification({
            title: "Вывод успешно завершен!",
            type: "success",
          });
          break;
        }
        case "PublisherWalletWithdrawal": {
          const available = Number(data?.eventInfo?.withdrawalAvailable);
          if (Number.isFinite(available)) {
            publisherStore.withdrawalAvailable = available;
          }
          ElNotification({
            title: "Выплата успешно завершена!",
            type: "success",
          });
          break;
        }
        // Паблишеру пора указать ссылку на опубликованный пост.
        // Глобально показываем только уведомление (на любой странице ЛК).
        // Саму кнопку «Указать ссылку» страница заказов рисует по данным с бэка
        // (статус + время publisherPost); сокет там нужен лишь для live-перерисовки.
        case "PublicationSetLinkNow": {
          const info = data?.eventInfo || data || {};
          // Показываем название канала, а не ссылку на него. Если названия нет —
          // откатываемся на ссылку, чтобы уведомление не осталось безымянным.
          const rawName = info.placeName || info.name || info.placeTitle || info.placeUrl;
          // Длинное название разносит уведомление по высоте — обрезаем.
          // 50 символов считаем вместе с многоточием.
          const NAME_LIMIT = 50;
          const fullName = rawName ? String(rawName) : "";
          const placeName =
            fullName.length > NAME_LIMIT
              ? `${fullName.slice(0, NAME_LIMIT - 3).trimEnd()}...`
              : fullName;
          // Название ведёт на опубликованный пост.
          const publicationUrl = info.publicationUrl;

          // Значения приходят с бэка и попадают в HTML — экранируем.
          const escapeHtml = (value: string) =>
            String(value).replace(
              /[&<>"']/g,
              (char) =>
                ({
                  "&": "&amp;",
                  "<": "&lt;",
                  ">": "&gt;",
                  '"': "&quot;",
                  "'": "&#39;",
                })[char] as string
            );

          let message = "Укажите ссылку на опубликованный пост в разделе «Заказы»";
          if (placeName) {
            const safeName = escapeHtml(placeName);
            const label = publicationUrl
              ? `<a href="${escapeHtml(publicationUrl)}" target="_blank" rel="noopener noreferrer" style="color:#3176FF">${safeName}</a>`
              : safeName;
            message = `Канал ${label}: укажите ссылку на опубликованный пост`;
          }

          ElNotification({
            title: "Пора указать ссылку на пост",
            message,
            dangerouslyUseHTMLString: true,
            type: "info",
            position: "top-right",
            duration: 0,
          });
          break;
        }
        // Реквизиты паблишера зарегистрированы в банке «Точка» (фоновая задача).
        // eventInfo.walletId — id кошелька, к которому относится событие.
        case "TochkaBeneficiarySuccess": {
          ElNotification({
            title: "Реквизиты успешно созданы!",
            message: "Теперь вы можете пополнить баланс",
            type: "success",
          });
          break;
        }
        case "TochkaBeneficiaryError": {
          ElNotification({
            title: "Реквизиты не прошли проверку!",
            message: 'Проверьте введенные данные в разделе "Баланс и документы"',
            type: "error",
          });
          break;
        }
        // Реквизиты проверены банком (фоновая задача): создаётся счёт.
        // В eventInfo.invoices — id и url счетов.
        case "NominalAccountInvoiceReady": {
          const info = data?.eventInfo || {};
          const invoices = Array.isArray(info.invoices) ? info.invoices : [];
          const rejected =
            info.success === false ||
            info.status === "rejected" ||
            info.status === "failed" ||
            info.rejected === true;
          if (rejected) {
            ElNotification({
              title: "Реквизиты не прошли проверку!",
              message: 'Проверьте введенные данные в разделе "Баланс и документы"',
              type: "error",
            });
          } else if (invoices.length) {
            ElNotification({
              title: "Счёт успешно создан!",
              message: 'Перейдите в раздел "Баланс и документы" для оплаты',
              type: "success",
            });
          } else {
            ElNotification({
              title: "Реквизиты успешно созданы!",
              message: "Теперь вы можете пополнить баланс",
              type: "success",
            });
          }
          break;
        }
        default:
          break;
      }
    });
  });
</script>

<style lang="scss">
  @import "public/assets/css/app-common.scss";
  @import "~/assets/flaticon-uicons/css/all/all.css";

  @import url("https://fonts.googleapis.com/css2?family=Nunito+Sans:ital,opsz,wght@0,6..12,200..1000;1,6..12,200..1000&display=swap");
  @import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

  @import "public/assets/css/font-faces.css";

  .size-сontainer {
    container-type: size;
  }

  .screen-wrapper {
    width: calc(100% - 80px);
    padding: 0px 6px;
    height: 100%;
    display: flex;
    flex-direction: column;

    @media (max-width: 578px) {
      width: 100%;
    }

    .screen-cols {
      @apply flex gap-3 items-stretch h-full;
      flex: 1;
    }
  }

  .container {
    @apply bg-white;
    border-radius: 24px;
  }

  .request-timeout-loading {
    pointer-events: none;
  }

  h1,
  h2 {
    color: $gray;
    font-weight: bold;
    line-height: 1.3;
  }

  .weight-md {
    @apply text-zinc-600;
  }

  label {
    @apply -mb-1 text-zinc-600;
  }

  p,
  span,
  button,
  label,
  div,
  input,
  textarea {
    div,
    p,
    span,
    button,
    label,
    div,
    input,
    input[type="text"],
    textarea,
    .error,
    input::placeholder,
    .el-radio.el-radio--large .el-radio__label,
    textarea::placeholder,
    label + label,
    .gray {
      font-size: 14px;
      font-weight: 400;
      font-family: Suisse Intl;
      color: #3a3d44;

      @media (max-width: 576px) {
        font-size: 14px;
      }
    }
  }

  #__nuxt {
    .screen-wrapper input,
    .el-select,
    .el-select__wrapper,
    .el-input {
      @apply outline-none border-primary/20 active:border-primary focus:border-primary;
      border-radius: 16px;

      &.error {
        color: inherit;
        border: 1px solid $red;
      }

      &.is-disabled {
        background-color: theme("colors.bgsidebar") !important;
        border: 1px solid theme("colors.brgray") !important;
        outline: 0 !important;
        box-shadow: none !important;
        opacity: 0.7;
      }
    }

    .text-danger {
      color: $red;
    }
  }

  textarea,
  textarea::placeholder {
    @apply rounded-xl
  transition-colors
  active:border-primary
  focus:border-primary
  border-sky-200;
  }

  textarea::placeholder {
    font-size: 12px;
  }

  .el-radio-group label.el-radio {
    margin-right: auto;
  }

  .el-radio {
    width: fit-content;
  }

  div.el-select__wrapper {
    border: 1px solid rgba($main, 0.5);
    border-radius: 8px;
    box-shadow: none;
  }

  li.is-disabled > span {
    cursor: not-allowed;
  }

  .el-message__content {
    white-space: pre-wrap;
    line-height: 1.3;
  }

  a,
  div.publications-table__advert,
  .publications-table__set-url,
  .button-link {
    cursor: pointer;
    color: theme("colors.primary");
    width: fit-content;
  }

  input,
  textarea,
  select {
    outline: 0;
    &:active,
    &:focus {
      outline: 0;
      --tw-ring-offset-color: none;
      --tw-ring-inset: none;
      --tw-ring-offset-color: none;
      --tw-ring-color: none;
    }
  }

  input,
  div.el-select__wrapper,
  div.el-select {
    width: 100%;

    .el-select__input {
      box-shadow: none;
    }
  }

  h3 {
    font-weight: 600;
  }

  .input-icon {
    position: absolute;
    top: 30%;
    left: 80%;
  }

  .el-select__suffix,
  label.el-checkbox {
    width: fit-content;
  }

  label.el-checkbox {
    margin-right: 8px;
    transform: scale(1.5);

    + label {
      font-weight: 600;
    }
  }

  .mobile-list-item {
    > div {
      font-size: 12px;
      color: $light-gray;

      > div:not(.publications-table__advert),
      span,
      b {
        color: $gray;
      }
    }
  }

  .text-light-gray {
    color: $light-gray;
  }

  .el-popper {
    width: fit-content;
  }

  .el-message-box {
    button.el-button--primary span {
      color: $white;
    }
  }

  .el-message-box__btns {
    button {
      width: fit-content;
    }
  }

  .spoiler {
    position: relative;
    color: transparent;
    overflow: hidden;
    pointer-events: none;
  }

  .spoiler::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url("https://advertiser.teleads.pro/_nuxt/giphy.DqAVwH0q.gif");
    background-position: 50%;
    background-size: 80px 80px;
    color: transparent;
    border-radius: 4px;
    filter: invert(1) opacity(0.8);
    opacity: 0.7;
    pointer-events: none;
  }

  .spoiler:hover {
    color: inherit;

    &::before {
      color: inherit;
      background: transparent;
    }
  }

  .showcase-description {
    p + p {
      margin-top: 0.5em;
    }

    blockquote {
      border-left: 2px solid #d9e5ff;
      padding-left: 12px;
    }
  }
</style>
